/**
 * Página pública de checklist de integración IT.
 *
 * Acceso vía URL /integration/{slug}?link={link_id}
 * protegida por password principal o por contraseña de perfil.
 * Tras autenticación el usuario puede ver y actualizar items
 * (cambiar estado, rellenar datos técnicos, añadir notas).
 * Los items mostrados se filtran según las categorías visibles del perfil.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import { Lock, FileCheck, Loader2, X, CheckCircle2, AlertTriangle, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL + "/api";

const ESTADO = {
  pendiente: { l: "Pendiente",  c: "bg-gray-100 text-gray-600" },
  en_curso:  { l: "En curso",   c: "bg-blue-100 text-blue-700" },
  bloqueado: { l: "Bloqueado",  c: "bg-rose-100 text-rose-700" },
  ok:        { l: "OK",         c: "bg-emerald-100 text-emerald-700" },
  na:        { l: "N/A",        c: "bg-slate-100 text-slate-500" },
};
const CRIT = {
  baja:     "bg-gray-100 text-gray-600",
  media:    "bg-amber-100 text-amber-700",
  alta:     "bg-orange-100 text-orange-700",
  crítica:  "bg-rose-100 text-rose-700",
};

const PROFILE_COLORS = {
  IT:              "bg-blue-100 text-blue-700",
  Jurídico:        "bg-purple-100 text-purple-700",
  Finanzas:        "bg-amber-100 text-amber-700",
  Compliance:      "bg-emerald-100 text-emerald-700",
  Administración:  "bg-gray-100 text-gray-700",
  Operaciones:     "bg-orange-100 text-orange-700",
  Dirección:       "bg-rose-100 text-rose-700",
  RRHH:            "bg-teal-100 text-teal-700",
};

export default function IntegrationPublic() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const linkId = searchParams.get("link");   // null = acceso global

  // Clave única por (slug + linkId) para soportar múltiples perfiles en el mismo navegador
  const tokKey = linkId ? `intit_tok_${slug}_${linkId}` : `intit_tok_${slug}`;

  const [token, setToken] = useState(() => sessionStorage.getItem(tokKey));
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) loadChecklist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadChecklist = async () => {
    setLoading(true);
    try {
      const params = linkId ? { link_id: linkId } : {};
      const r = await axios.get(`${API_BASE}/integration/public/${slug}`, {
        headers: { "X-Integration-Token": token },
        params,
      });
      setChecklist(r.data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem(tokKey);
        setToken(null);
      }
    } finally { setLoading(false); }
  };

  const authenticate = async (password) => {
    try {
      const body = { password };
      if (linkId) body.link_id = linkId;
      const r = await axios.post(`${API_BASE}/integration/public/${slug}/auth`, body);
      sessionStorage.setItem(tokKey, r.data.token);
      setToken(r.data.token);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Contraseña incorrecta");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(tokKey);
    setToken(null);
    setChecklist(null);
  };

  const updateItem = async (iid, patch) => {
    try {
      const params = linkId ? { link_id: linkId } : {};
      await axios.put(
        `${API_BASE}/integration/public/${slug}/items/${iid}`,
        patch,
        { headers: { "X-Integration-Token": token }, params },
      );
      toast.success("Guardado");
      loadChecklist();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  if (!token) return <AuthGate onSubmit={authenticate} isProfileLink={!!linkId}/>;
  if (loading && !checklist) return (
    <div className="min-h-screen flex items-center justify-center text-gray-500 text-sm">
      <Loader2 className="animate-spin mr-2" size={14}/> Cargando…
    </div>
  );
  if (!checklist) return <AuthGate onSubmit={authenticate} isProfileLink={!!linkId}/>;

  return <PublicChecklist data={checklist} onUpdate={updateItem} onLogout={logout}/>;
}

function AuthGate({ onSubmit, isProfileLink }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    setBusy(true);
    await onSubmit(pw);
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#007934] to-[#00572a] flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4" data-testid="intit-public-auth">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#007934]/10 rounded-full mb-2">
            <Lock size={24} className="text-[#007934]"/>
          </div>
          <h1 className="text-lg font-bold text-gray-900">Integración IT · Valoriza</h1>
          <p className="text-xs text-gray-500 mt-1">
            {isProfileLink
              ? "Acceso por perfil · introduce tu contraseña"
              : "Checklist privado · introduce la contraseña"}
          </p>
          {isProfileLink && (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-0.5">
              <ShieldCheck size={11}/> Vista personalizada por perfil
            </div>
          )}
        </div>
        <input
          type="password"
          autoFocus
          value={pw}
          onChange={e=>setPw(e.target.value)}
          placeholder="Contraseña"
          className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
          data-testid="intit-public-password"
        />
        <button
          type="submit"
          disabled={busy || !pw}
          className="w-full bg-[#007934] hover:bg-[#00572a] text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="intit-public-submit">
          {busy ? <><Loader2 size={14} className="animate-spin"/> Accediendo…</> : "Acceder"}
        </button>
        <p className="text-[10px] text-gray-400 text-center">Si no tienes la contraseña, contacta con el equipo TIC de Valoriza.</p>
      </form>
    </div>
  );
}

function PublicChecklist({ data, onUpdate, onLogout }) {
  const items = data.items || [];
  const done    = items.filter(i => i.estado === "ok").length;
  const blocked = items.filter(i => i.estado === "bloqueado").length;
  const progress = items.length ? Math.round(100 * done / items.length) : 0;

  const byCategory = {};
  items.forEach(i => { (byCategory[i.category] ||= []).push(i); });

  const [expandedId, setExpandedId] = useState(null);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-[#007934] to-[#00572a] text-white p-5">
        <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] opacity-80 uppercase tracking-wide">VALORIZA · Integración IT</p>
            <h1 className="text-lg font-bold">{data.name}</h1>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <p className="text-xs opacity-90">{data.acquired_company}</p>
              {data.profile_label && (
                <span className="text-[10px] bg-white/20 rounded-full px-2 py-0.5 font-semibold">
                  Vista: {data.profile_label}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-[10px] opacity-80 uppercase">Progreso</p>
              <p className="text-2xl font-bold leading-none">{progress}%</p>
              <p className="text-[10px] opacity-80">{done}/{items.length} OK{blocked>0?` · ${blocked} bloqueados`:""}</p>
            </div>
            <button onClick={onLogout} className="bg-white/15 hover:bg-white/25 text-white text-xs px-3 py-1.5 rounded flex items-center gap-1" data-testid="intit-public-logout">
              <LogOut size={12}/> Salir
            </button>
          </div>
        </div>
        <div className="max-w-5xl mx-auto mt-3">
          <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all" style={{ width: `${progress}%` }}/>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 space-y-5" data-testid="intit-public-main">
        {Object.entries(byCategory).map(([cat, its]) => {
          const catDone = its.filter(i => i.estado === "ok").length;
          return (
            <section key={cat} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b flex items-center justify-between">
                <h2 className="text-sm font-bold text-[#007934]">{cat}</h2>
                <span className="text-[10px] text-gray-500 font-semibold">{catDone}/{its.length} completados</span>
              </div>
              <div className="divide-y">
                {its.map(i => (
                  <div key={i.id} className="p-3 hover:bg-gray-50 cursor-pointer" onClick={()=>setExpandedId(expandedId===i.id?null:i.id)} data-testid={`intit-pub-item-${i.id}`}>
                    <div className="flex items-start gap-2">
                      <select
                        value={i.estado || "pendiente"}
                        onClick={e=>e.stopPropagation()}
                        onChange={e=>onUpdate(i.id, {estado: e.target.value})}
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded border-0 cursor-pointer ${ESTADO[i.estado]?.c || "bg-gray-100"}`}
                        data-testid={`intit-pub-status-${i.id}`}>
                        {Object.entries(ESTADO).map(([v, e]) => <option key={v} value={v}>{e.l}</option>)}
                      </select>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${CRIT[i.criticidad] || "bg-gray-100"}`}>{i.criticidad}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-900">{i.title}</p>
                        {i.subcategory && <p className="text-[10px] text-gray-500">{i.subcategory}</p>}
                        {i.description && <p className="text-[10px] text-gray-500 mt-1">{i.description}</p>}
                      </div>
                      <div className="text-[9px] text-right text-gray-500 whitespace-nowrap hidden md:block">
                        {i.owner_tecnico && <div>👤 {i.owner_tecnico}</div>}
                      </div>
                    </div>
                    {expandedId === i.id && (
                      <div className="mt-3 pt-3 border-t grid grid-cols-1 md:grid-cols-2 gap-2 text-xs" onClick={e=>e.stopPropagation()}>
                        <PublicField label="Proveedor" v={i.proveedor} on={v=>onUpdate(i.id,{proveedor:v})}/>
                        <PublicField label="Contrato" v={i.contrato} on={v=>onUpdate(i.id,{contrato:v})}/>
                        <PublicField label="Usuarios" v={i.usuarios} on={v=>onUpdate(i.id,{usuarios:v})}/>
                        <PublicField label="Licencias" v={i.licencias} on={v=>onUpdate(i.id,{licencias:v})}/>
                        <PublicField label="Ubicación" v={i.ubicacion} on={v=>onUpdate(i.id,{ubicacion:v})}/>
                        <PublicField label="Modalidad" v={i.modalidad_alojamiento} on={v=>onUpdate(i.id,{modalidad_alojamiento:v})} placeholder="On-prem · Cloud"/>
                        <PublicField label="Integraciones" span={2} v={i.integraciones} on={v=>onUpdate(i.id,{integraciones:v})}/>
                        <PublicField label="Backups" v={i.backups} on={v=>onUpdate(i.id,{backups:v})}/>
                        <PublicField label="Plan continuidad" v={i.plan_continuidad} on={v=>onUpdate(i.id,{plan_continuidad:v})}/>
                        <PublicField label="Notas" span={2} v={i.notas} on={v=>onUpdate(i.id,{notas:v})} textarea/>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          );
        })}
        <div className="text-[10px] text-center text-gray-400 py-6">
          Valoriza Servicios Medioambientales · Plataforma corporativa · Confidencial
        </div>
      </main>
    </div>
  );
}

function PublicField({ label, v, on, span, placeholder, textarea }) {
  const [val, setVal] = useState(v || "");
  useEffect(() => setVal(v || ""), [v]);
  const commit = () => { if (val !== (v||"")) on(val); };
  return (
    <label className={`block ${span===2?"md:col-span-2":""}`}>
      <span className="text-[9px] uppercase text-gray-500 font-semibold">{label}</span>
      {textarea
        ? <textarea value={val} onChange={e=>setVal(e.target.value)} onBlur={commit} rows={2} placeholder={placeholder} className="w-full border rounded px-2 py-1 mt-0.5 text-xs"/>
        : <input value={val} onChange={e=>setVal(e.target.value)} onBlur={commit} placeholder={placeholder} className="w-full border rounded px-2 py-1 mt-0.5 text-xs"/>
      }
    </label>
  );
}
